import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Phone, Users, Monitor, Mail, CheckCircle2 } from 'lucide-react';
import crm, { TASK_TYPES } from '../crm/api';
import LeadDetail from '../crm/LeadDetail';

// /crm-api/tasks is owner-scoped by the API, so this is only ever this rep's work.
const FILTERS = [
  { key: 'today', label: 'Due today' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'open', label: 'All open' },
  { key: 'done', label: 'Done' },
];
const ICON = { call: Phone, meeting: Users, demo: Monitor, email: Mail, todo: CheckCircle2 };
const typeLabel = (t) => (TASK_TYPES.find((x) => x.key === t) || {}).label || t;
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'No date');

export default function EmployeeTasks() {
  const [filter, setFilter] = useState('today');
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    crm.get('/tasks', { params: { filter } })
      .then(({ data }) => setTasks(data.tasks || []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
    // counts for the filter chips
    Promise.all(FILTERS.map((f) => crm.get('/tasks', { params: { filter: f.key } }).then(({ data }) => [f.key, (data.tasks || []).length]).catch(() => [f.key, 0])))
      .then((pairs) => setCounts(Object.fromEntries(pairs)));
  }, [filter]);
  useEffect(load, [load]);

  const toggle = async (t) => { await crm.put(`/tasks/${t.id}`, { done: !t.done }); load(); };

  return (
    <div className="space-y-4 max-w-[1180px] pt-1">
      <div>
        <h2 className="text-[20px] font-extrabold text-[var(--heading)] tracking-tight">Follow-ups</h2>
        <p className="text-[13px] text-[var(--muted)]">Every call, visit and demo you owe someone.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const on = filter === f.key;
          const overdue = f.key === 'overdue' && counts.overdue > 0;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className="text-[13px] font-bold px-3.5 py-2 rounded-xl border transition-colors"
              style={on
                ? { background: 'var(--acc)', borderColor: 'var(--acc)', color: '#fff' }
                : { borderColor: overdue ? '#dc2626' : 'var(--border)', color: overdue ? '#dc2626' : 'var(--muted)' }}>
              {f.label}{counts[f.key] !== undefined && <span className="ml-1.5 opacity-70 tabular-nums">{counts[f.key]}</span>}
            </button>
          );
        })}
      </div>

      <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-[var(--muted)] text-sm">
            <Loader2 size={15} className="animate-spin" /> Loading…
          </div>
        ) : !tasks.length ? (
          <div className="p-12 text-center">
            <div className="text-[15px] font-bold text-[var(--text)]">Nothing here 🎉</div>
            <div className="text-[13px] text-[var(--muted)] mt-1">
              {filter === 'overdue' ? 'No overdue follow-ups — you are on top of it.' : 'Add follow-ups from any lead.'}
            </div>
          </div>
        ) : (
          <div>
            {tasks.map((t) => {
              const Icon = ICON[t.type] || CheckCircle2;
              const late = !t.done && t.dueDate && t.dueDate < new Date().toISOString().slice(0, 10);
              return (
                <div key={t.id} className="flex items-center gap-3.5 px-5 py-3.5 border-b border-[var(--border)] last:border-0 hover:bg-[var(--soft)] transition-colors">
                  <button onClick={() => toggle(t)} aria-label={t.done ? 'Reopen' : 'Mark done'}
                    className="w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center transition-colors"
                    style={t.done ? { background: 'var(--acc)', borderColor: 'var(--acc)' } : { borderColor: 'var(--muted)' }}>
                    {t.done && <span className="text-white text-[11px] font-black leading-none">✓</span>}
                  </button>
                  <Icon size={16} className="shrink-0" style={{ color: late ? '#dc2626' : 'var(--muted)' }} />
                  <div className="min-w-0 flex-1">
                    <div className={`text-[14px] font-bold truncate ${t.done ? 'line-through' : ''}`} style={{ color: t.done ? 'var(--muted)' : 'var(--text)' }}>{t.title}</div>
                    <div className="text-[11.5px] text-[var(--muted)] truncate">
                      {typeLabel(t.type)}
                      {t.Lead && <> · <button onClick={() => setOpenId(t.Lead.id)} className="hover:underline font-semibold">{t.Lead.name}</button></>}
                    </div>
                  </div>
                  <div className="text-[12px] font-bold tabular-nums shrink-0" style={{ color: late ? '#dc2626' : 'var(--muted)' }}>
                    {late ? 'Overdue · ' : ''}{fmt(t.dueDate)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {openId && <LeadDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
